import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { DateTime } from "luxon";
import { TextEditor } from "./editor";
import { useStyles } from "./termSheet.style";
import { TermSheetHeader } from "./termSheetHeader";
import TermSheetRequirement from "./termSheetRequirement";
import TermSheetTable from "./termSheetTable";
import TermSheetFooter from "./termSheetFooter";
import { TenantLogo } from "./TenantLogo";

import TermSheetEmailTemplate from "./TermSheetEmailTemplate";
import TermSheetFeeDetails from "./termSheetFeeDetails";
import DisclaimerPopUp from "./disclaimerPopUp";

const TermSheet = (props) => {
  const classes = useStyles();
  const openDisclaimer = true;
  const {
    termSheetData,
    setTermSheetData,
    isUserConfig = true,
    permissionsToRenderInUI,
    termSheetConfig = {},
    termSheetAction,
    emailBodyChange,
    sanitizeCurrency,
    isTermsheetAlreadySaved,
    isDataEdited
  } = props;
  const [subjectValue, setSubjectValue] = useState(
    termSheetConfig?.email?.subject ?? ""
  );

  useEffect(() => {
    if (["edit", "regenerate"].includes(termSheetAction))
      setSubjectValue(termSheetData?.emails?.subject);
  }, [termSheetAction]);

  const {
    showEmailSection = false,
    showLoanDetails = false,
    showFeeDetails = false,
    showEmailBody = false,
    isFullView = false,
    showMerchantsMortgage = false,
    showRequirements = true
  } = permissionsToRenderInUI;

  const widthHeight = useMemo(() => {
    if (isFullView) {
      return "100%";
    }
    if (isUserConfig) {
      return "800px";
    }
    return "700px";
  }, [isUserConfig, isFullView]);

  return (
    <Grid
      data-testid='select-term-sheet-dropdown'
      style={{
        width: widthHeight
      }}
    >
      {termSheetConfig.termSheetAction === "regenerate" ? (
        <DisclaimerPopUp openDisclaimer={openDisclaimer} />
      ) : null}
      {isUserConfig && (
        <TermSheetHeader
          termSheetData={termSheetData}
          termSheetConfig={termSheetConfig}
        />
      )}
      <Grid sx={{ margin: "20px" }}>
        <Grid className={classes.warning}>
          <ErrorOutlineIcon fontSize='small' /> &ensp;
          <Typography>
            This term sheet letter is for discussion purposes. It is not a
            commitment to lend money.
          </Typography>
        </Grid>
        {Object.keys(termSheetConfig)?.length && termSheetConfig?.originatorLogo ? (
          <Grid container>
            <img src={termSheetConfig?.originatorLogo} alt='logo' width={140} height={40} />
          </Grid>
        ) : null}
        {!isUserConfig && showMerchantsMortgage && (
          <Grid sx={{ marginTop: "20px" }}>
            <TenantLogo />
            <Typography sx={{ fontSize: "16px" }}>
              Merchants Mortgage <br /> & Trust Corporation
            </Typography>
          </Grid>
        )}
        <Typography className={classes.headerLabel}>Term Sheet</Typography>
      </Grid>
      {isUserConfig && (
        <>
          <Grid style={{ padding: "0px 20px" }}>
            <Typography className={classes.dateContainer}>
              <span className={classes.dateLabel}>Date:</span>
              {DateTime.local().toFormat("dd/MM/yyyy")}
            </Typography>
          </Grid>
          <Grid className={classes.toContainer}>
            <span className={classes.toLabel}>To:</span>
            {termSheetData &&
              termSheetData?.borrowers?.map((borrower) => {
                return (
                  borrower?.email && (
                    <span className={classes.emailLabel}>
                      {borrower?.email}
                    </span>
                  )
                );
              })}
          </Grid>
          <Grid style={{ padding: "0px 20px" }}>
            <Typography className={classes.subjectContainer}>
              <span className={classes.subjectLabel}>RE:</span>
              {termSheetData?.subject}
            </Typography>
          </Grid>
        </>
      )}
      {/* OS - Term Sheet changes */}
      {showEmailSection && (
        <>
          <TermSheetEmailTemplate
            termSheetConfig={termSheetConfig}
            termSheetData={termSheetData}
            triggeredSendEmail={props.triggeredSendEmail}
          />
          <Grid style={{ padding: "0px 12px" }}>
            {["view", "download", "view-from-list"].includes(
              termSheetAction
            ) ? (
              <>
                <span
                  className={classes.subjectLabel}
                  style={{ padding: "0px 8px" }}
                >
                  Subject:
                </span>
                {termSheetData?.emails?.subject}
              </>
            ) : (
              <Typography className={classes.subjectContainer}>
                <TextField
                  className={classes.customTextField}
                  variant='outlined'
                  fullWidth
                  value={subjectValue}
                  onChange={(e) => {
                    return setSubjectValue(e.target.value);
                  }}
                  onBlur={() => {
                    return termSheetConfig?.email?.handleSubjectChange(
                      subjectValue,
                      termSheetData
                    );
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Box className={classes.labelBox}>
                          <span>Subject</span>
                        </Box>
                        <Box className={classes.verticalBar} />
                        <Box className={classes.chipContainer} />
                      </InputAdornment>
                    )
                  }}
                />
              </Typography>
            )}
          </Grid>
          <br />
          <Grid style={{ padding: "0px 20px" }}>
            <Typography className={classes.subjectContainer}>
              <span className={classes.subjectLabel}>Date:</span>
              {termSheetData?.date}
            </Typography>
          </Grid>
        </>
      )}

      <Grid className={classes.emailContainer}>
        {(isUserConfig || showEmailBody) && !termSheetConfig?.isViewMode ? (
          <TextEditor
            initialValue={termSheetData?.emailBody}
            emailBodyChange={emailBodyChange}
            tinyMceKey={props?.termSheetConfig?.tinyMceKey}
            requiredToolBars=''
          />
        ) : (
          // eslint-disable-next-line react/no-danger
          <div dangerouslySetInnerHTML={{ __html: termSheetData?.emailBody }} />
        )}
      </Grid>

      <Grid className={classes.emailContainer}>
        {showLoanDetails ? (
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              sx={{
                border: "1px solid #e1e8ee",
                padding: "8px",
                alignItem: "center"
              }}
            >
              <Typography className={classes.requirementTitle}>
                Loan Details
              </Typography>
            </Grid>
            {termSheetData?.loanData?.map((loan) => {
              return (
                <Grid item xs={6} key={loan?.label}>
                  <TermSheetTable loanData={loan} />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Grid container>
            <Grid item xs={12}>
              <TermSheetTable loanData={termSheetData.loanData} />
            </Grid>
          </Grid>
        )}
      </Grid>

      {termSheetData?.propertyAddressDetails &&
      termSheetData?.propertyAddressDetails?.length > 0 ? (
        <Grid className={classes.emailContainer}>
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              sx={{
                border: "1px solid #e1e8ee",
                padding: "8px",
                alignItem: "center"
              }}
            >
              <Typography className={classes.requirementTitle}>
                Property Address Details
              </Typography>
            </Grid>
            {/* {termSheetData?.suggestedLoanTerms?.map((loan) => {
              return ( */}
            <Grid item xs={12}>
              <TermSheetTable
                loanData={termSheetData?.propertyAddressDetails}
                customWidth={{ label: 3.5, value: 8 }}
              />
            </Grid>
            {/* );
            })} */}
          </Grid>
        </Grid>
      ) : null}

      {termSheetData?.suggestedLoanTerms?.length && showLoanDetails ? (
        <Grid className={classes.emailContainer}>
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              sx={{
                border: "1px solid #e1e8ee",
                padding: "8px",
                alignItem: "center"
              }}
            >
              <Typography className={classes.requirementTitle}>
                Suggested Loan Terms
              </Typography>
            </Grid>
            {termSheetData?.suggestedLoanTerms?.map((loan) => {
              return (
                <Grid item xs={6} key={loan?.label}>
                  <TermSheetTable loanData={loan} />
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      ) : null}
      {termSheetData?.feeDetails && showFeeDetails ? (
        <Grid className={classes.emailContainer}>
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              sx={{
                border: "1px solid #e1e8ee",
                padding: "8px",
                alignItem: "center"
              }}
            >
              <Typography className={classes.requirementTitle}>
                Estimated Fee Details
              </Typography>
            </Grid>
            <TermSheetFeeDetails
              feeDetails={termSheetData?.feeDetails}
              termSheetConfig={termSheetConfig}
              setOpenFeeDrawer={props.setOpenFeeDrawer}
              openFeeDrawer={props.openFeeDrawer}
              sanitizeCurrency={sanitizeCurrency}
            />
          </Grid>
        </Grid>
      ) : null}
      <TermSheetRequirement
        termSheetData={termSheetData}
        setTermSheetData={setTermSheetData}
        isUserConfig={isUserConfig || showRequirements}
        termSheetConfig={termSheetConfig}
      />
      <TermSheetFooter
        isSignatureRequired
        permissionsToRenderInUI={permissionsToRenderInUI}
        termSheetConfig={termSheetConfig}
        termSheetAction={termSheetAction}
        termSheetData={termSheetData}
        isTermsheetAlreadySaved={isTermsheetAlreadySaved}
        isDataEdited={isDataEdited}
      />
    </Grid>
  );
};

TermSheet.propTypes = {
  isDataEdited: PropTypes.bool.isRequired,
  isTermsheetAlreadySaved: PropTypes.bool.isRequired,
  sanitizeCurrency: PropTypes.func.isRequired,
  termSheetData: PropTypes.shape({
    propertyAddressDetails: PropTypes,
    date: PropTypes.string,
    emails: PropTypes,
    loanData: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired
        // Add other properties as needed
      })
    ).isRequired,
    suggestedLoanTerms: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        term: PropTypes.string.isRequired
        // Add other properties as needed
      })
    ),
    feeDetails: PropTypes.shape({
      feeType: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired
      // Add other properties as needed
    }),
    emailBody: PropTypes.string,
    subject: PropTypes.string,
    borrowers: PropTypes.arrayOf(
      PropTypes.shape({
        email: PropTypes.string
      })
    )
  }).isRequired,
  setTermSheetData: PropTypes.func.isRequired,
  emailBodyChange: PropTypes.func.isRequired,
  permissionsToRenderInUI: PropTypes.shape({
    showEmailSection: PropTypes.bool,
    showLoanDetails: PropTypes.bool,
    showEmailBody: PropTypes.bool,
    showFeeDetails: PropTypes.bool,
    isFullView: PropTypes.bool,
    showMerchantsMortgage: PropTypes.bool,
    showRequirements: PropTypes.bool,
    showFooterActions: PropTypes.bool
  }).isRequired,
  isUserConfig: PropTypes.bool,

  termSheetConfig: PropTypes.shape({
    tinyMceKey: PropTypes.string,
    termSheetAction: PropTypes.string,
    email: PropTypes.shape({
      date: PropTypes.string,
      subject: PropTypes.string
    }),
    isViewMode: PropTypes.bool,
    feeDetails: PropTypes.shape({
      config: PropTypes.shape({}).isRequired,
      handleFeeChange: PropTypes.func.isRequired,
      handleDeleteFee: PropTypes.func.isRequired,
      handleAddFee: PropTypes.func.isRequired,
      handleAddNewFeeConfig: PropTypes.func.isRequired,
      handleVisibiltyChange: PropTypes.func.isRequired
    })
  }),
  termSheetAction: PropTypes.string.isRequired,
  originatorLogo: PropTypes.string,
  triggeredSendEmail: PropTypes.bool,
  setOpenFeeDrawer: PropTypes.func,
  openFeeDrawer: PropTypes.bool
};

TermSheet.defaultProps = {
  isUserConfig: false,
  openFeeDrawer: false,
  termSheetConfig: {},
  originatorLogo: "",
  triggeredSendEmail: false,
  setOpenFeeDrawer: () => {}
};

export default React.memo(TermSheet);
